import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Alert,
  Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, Grid, Collapse, Stack,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import { formatDistanceToNow } from 'date-fns';
import ImageViewer from '../components/ImageViewer';
import StreamViewer from '../components/StreamViewer';
import { getDevice, getImageUrl, requestDeviceSetup, getAlarmStats, getAlarmConfig } from '../api/client';
import useDetectionSettings, { enabled } from '../hooks/useDetectionSettings';
import useTimezone from '../hooks/useTimezone';
import { formatDateTime } from '../utils/format';

const TYPE_COLORS = {
  drowsiness: '#f44336',
  yawning: '#ff9800',
  both: '#9c27b0',
};

export default function DevicePage() {
  const detectionSettings = useDetectionSettings();
  const tz = useTimezone();
  const { id } = useParams();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageOpen, setImageOpen] = useState(false);
  const [streamOpen, setStreamOpen] = useState(false);
  const [setupMsg, setSetupMsg] = useState(null);
  const [requestingSetup, setRequestingSetup] = useState(false);
  const [alarmStats, setAlarmStats] = useState(null);
  const [alarmConfig, setAlarmConfig] = useState(null);

  useEffect(() => {
    setLoading(true);
    getDevice(id)
      .then(setDevice)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    getAlarmStats(id).then(setAlarmStats).catch(() => setAlarmStats({}));
    // Vendor-wide effective config — devices don't have per-device overrides
    // configurable from the UI yet, so this is what this device actually runs.
    getAlarmConfig().then(setAlarmConfig).catch(() => setAlarmConfig({}));
  }, [id]);

  const handleRequestSetup = async () => {
    if (!window.confirm(
      'This will clear the saved auto-login on this device and show the ' +
      'login screen again next time it checks in (usually within a minute). ' +
      'Whoever logs in there will become the new saved operator. Continue?'
    )) return;
    setRequestingSetup(true);
    setSetupMsg(null);
    try {
      await requestDeviceSetup(id);
      setSetupMsg({ severity: 'success', text: 'Requested — the login screen will appear on this device shortly.' });
    } catch (e) {
      setSetupMsg({ severity: 'error', text: e.response?.data?.detail || 'Request failed.' });
    } finally {
      setRequestingSetup(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
  if (!device) return null;

  const statusColor = device.is_online ? 'success' : 'default';

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/')}
        sx={{ mb: 2 }}
      >
        Back to Fleet
      </Button>

      {/* Device header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="h5" fontWeight={700}>
            {device.hostname || 'Unknown Host'}
          </Typography>
          <Chip
            label={device.is_online ? 'Online' : 'Offline'}
            color={statusColor}
            size="small"
          />
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<PersonSearchIcon />}
              onClick={handleRequestSetup}
              disabled={requestingSetup}
              size="small"
            >
              Request Setup Screen
            </Button>
            <Button
              variant={streamOpen ? 'contained' : 'outlined'}
              startIcon={streamOpen ? <VideocamOffIcon /> : <VideocamIcon />}
              onClick={() => setStreamOpen((v) => !v)}
              disabled={!device.is_online}
              size="small"
            >
              {streamOpen ? 'Stop Live View' : 'View Live'}
            </Button>
          </Box>
        </Box>

        {setupMsg && (
          <Alert severity={setupMsg.severity} sx={{ mb: 2 }} onClose={() => setSetupMsg(null)}>
            {setupMsg.text}
          </Alert>
        )}

        {/* Live stream panel — only mounted when open (disconnects when closed) */}
        <Collapse in={streamOpen} unmountOnExit>
          <Box sx={{ mb: 2 }}>
            <StreamViewer
              deviceId={id}
              deviceName={device.hostname}
              height={360}
              showOverlay
            />
          </Box>
        </Collapse>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="text.secondary">IP Address</Typography>
            <Typography variant="body1">{device.ip_address || '—'}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="text.secondary">Camera Status</Typography>
            <Typography variant="body1">{device.camera_status || '—'}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="text.secondary">Last Seen</Typography>
            <Typography variant="body1">
              {device.last_seen_at
                ? formatDistanceToNow(new Date(device.last_seen_at), { addSuffix: true })
                : 'Never'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="text.secondary">CPU</Typography>
            <Typography variant="body1">
              {device.cpu_percent != null ? `${device.cpu_percent.toFixed(1)}%` : '—'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="text.secondary">Memory</Typography>
            <Typography variant="body1">
              {device.memory_mb != null ? `${device.memory_mb.toFixed(0)} MB` : '—'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="text.secondary">Detection FPS</Typography>
            <Typography variant="body1">
              {device.fps != null ? `${device.fps.toFixed(1)}` : '—'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} mb={1}>Alarm Configuration &amp; Health</Typography>
        <Grid container spacing={2} mb={2}>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">Sync Status</Typography>
            <Typography variant="body1">{device.alarm_sync_status || '—'}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">Last Sync</Typography>
            <Typography variant="body1">
              {device.alarm_last_sync_at
                ? formatDistanceToNow(new Date(device.alarm_last_sync_at), { addSuffix: true })
                : 'Never'}
            </Typography>
          </Grid>
        </Grid>
        {device.assigned_features && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              Assigned Features
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {Object.entries(device.assigned_features).map(([feature, isAssigned]) => (
                <Chip key={feature} size="small"
                  label={`${isAssigned ? '✓' : '✗'} ${feature.charAt(0).toUpperCase()}${feature.slice(1)}`}
                  color={isAssigned ? 'success' : 'default'}
                  variant={isAssigned ? 'filled' : 'outlined'} />
              ))}
            </Stack>
          </Box>
        )}
        <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Feature</strong></TableCell>
              <TableCell><strong>Feature Enabled</strong></TableCell>
              <TableCell><strong>Alarm Enabled</strong></TableCell>
              <TableCell><strong>Delay</strong></TableCell>
              <TableCell><strong>Cooldown</strong></TableCell>
              <TableCell><strong>Volume</strong></TableCell>
              <TableCell><strong>Audio</strong></TableCell>
              <TableCell><strong>Config Version</strong></TableCell>
              <TableCell><strong>Playback Status</strong></TableCell>
              <TableCell><strong>Last Played</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {['drowsiness', 'yawning', 'distracted', 'presence'].map((feature) => {
              const s = alarmStats?.[feature];
              const c = alarmConfig?.[feature];
              if (!c) {
                return (
                  <TableRow key={feature} hover>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{feature}</TableCell>
                    <TableCell colSpan={9}>
                      <Typography variant="caption" color="text.secondary">Not licensed for this vendor</Typography>
                    </TableCell>
                  </TableRow>
                );
              }
              return (
                <TableRow key={feature} hover>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{feature}</TableCell>
                  <TableCell>
                    <Chip size="small" label={c.is_enabled ? 'On' : 'Off'}
                          color={c.is_enabled ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={c.alarm_enabled ? 'On' : 'Off'}
                          color={c.alarm_enabled ? 'success' : 'default'} variant="outlined" />
                  </TableCell>
                  <TableCell>{c.trigger_delay_seconds}s</TableCell>
                  <TableCell>{c.cooldown_seconds}s</TableCell>
                  <TableCell>{c.volume_percent}%</TableCell>
                  <TableCell>
                    {c.audio ? `${c.audio.original_filename} (v${c.audio.version})` : 'Default sound'}
                  </TableCell>
                  <TableCell>v{c.config_version}</TableCell>
                  <TableCell>
                    {s ? <Chip size="small" label={s.current_state}
                              color={s.current_state === 'playing' ? 'error' : 'default'} /> : '—'}
                  </TableCell>
                  <TableCell>
                    {s?.last_played_at ? formatDateTime(s.last_played_at, tz) : '—'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </TableContainer>
      </Paper>

      {enabled(detectionSettings, 'presence') && device.presence && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" fontWeight={600}>Presence Monitoring</Typography>
          <Grid container spacing={2} mt={0}>
            <Grid item xs={6} sm={3}><Typography variant="caption">Current Status</Typography><Typography>{device.presence.current_status?.replace('_', ' ')}</Typography></Grid>
            <Grid item xs={6} sm={3}><Typography variant="caption">Presence</Typography><Typography>{device.presence.presence_percent}%</Typography></Grid>
            <Grid item xs={6} sm={3}><Typography variant="caption">Away Sessions</Typography><Typography>{device.presence.away_sessions}</Typography></Grid>
            <Grid item xs={6} sm={3}><Typography variant="caption">Current Away</Typography><Typography>{Math.floor((device.presence.current_away_seconds || 0) / 60)} min</Typography></Grid>
          </Grid>
        </Paper>
      )}

      {/* Recent events */}
      <Typography variant="h6" fontWeight={600} mb={2}>
        Recent Events ({device.recent_events?.length ?? 0})
      </Typography>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Date / Time</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell><strong>Duration</strong></TableCell>
              <TableCell><strong>EAR</strong></TableCell>
              <TableCell><strong>Image</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(device.recent_events || []).map((event) => (
              <TableRow key={event.id} hover>
                <TableCell>
                  {formatDateTime(event.started_at, tz)}
                </TableCell>
                <TableCell>
                  <Chip
                    label={event.detection_type}
                    size="small"
                    sx={{
                      bgcolor: TYPE_COLORS[event.detection_type] || '#757575',
                      color: 'white',
                      fontSize: '0.7rem',
                    }}
                  />
                </TableCell>
                <TableCell>
                  {event.duration_seconds ? `${event.duration_seconds.toFixed(1)}s` : '—'}
                </TableCell>
                <TableCell>{event.ear_value?.toFixed(3) ?? '—'}</TableCell>
                <TableCell>
                  {event.image_ref ? (
                    <Button
                      size="small"
                      onClick={() => {
                        setImageUrl(getImageUrl(event.image_ref));
                        setImageOpen(true);
                      }}
                    >
                      View
                    </Button>
                  ) : '—'}
                </TableCell>
              </TableRow>
            ))}
            {!device.recent_events?.length && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">No events recorded</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ImageViewer
        open={imageOpen}
        imageUrl={imageUrl}
        onClose={() => setImageOpen(false)}
      />
    </Box>
  );
}
